/**
 * FleetView Component
 * Extracted from HubDashboard.jsx for better code organization
 * Handles: Fleet management - trucks, drivers, locations, truck stops, mechanics, offices
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Truck, Users, MapPin, Building, Wrench, Plus, Upload, Trash2, RefreshCw, FileText, ClipboardPaste
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FleetView() {
  const [stats, setStats] = useState({ trucks: 0, drivers: 0 });
  const [loading, setLoading] = useState(true);
  const [fleetTab, setFleetTab] = useState('overview');
  const [items, setItems] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [addForm, setAddForm] = useState({});
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [importing, setImporting] = useState(false);
  const [bulkImportType, setBulkImportType] = useState('trucks'); // 'trucks' or 'drivers'

  const entityTypes = [
    { key: 'trucks', label: 'Trucks', icon: Truck, color: 'orange', fields: ['truck_number', 'vin', 'make', 'model', 'year', 'plate_number', 'status'] },
    { key: 'drivers', label: 'Drivers', icon: Users, color: 'cyan', fields: ['name', 'phone', 'email', 'license_number', 'license_expiry', 'status'] },
    { key: 'locations', label: 'Locations', icon: MapPin, color: 'green', fields: ['name', 'address', 'city', 'state', 'zip', 'type'] },
    { key: 'truck-stops', label: 'Truck Stops', icon: Building, color: 'blue', fields: ['name', 'address', 'city', 'state', 'amenities'] },
    { key: 'mechanics', label: 'Mechanics', icon: Wrench, color: 'purple', fields: ['name', 'email', 'phone', 'specialization', 'certification'] },
    { key: 'offices', label: 'Offices', icon: Building, color: 'pink', fields: ['name', 'address', 'city', 'state', 'phone', 'manager'] },
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const statsRes = await axios.get(`${API}/fleet/stats`, { withCredentials: true });
        setStats(statsRes.data);
        
        if (fleetTab !== 'overview') {
          const itemsRes = await axios.get(`${API}/fleet/${fleetTab}`, { withCredentials: true });
          const entityKey = fleetTab.replace('-', '_');
          setItems(itemsRes.data[entityKey] || itemsRes.data[fleetTab] || []);
        }
      } catch (e) {
        console.error('Failed to load fleet data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [fleetTab]);

  const handleAdd = async () => {
    try {
      await axios.post(`${API}/fleet/${fleetTab}`, addForm, { withCredentials: true });
      toast.success('Added successfully!');
      setShowAddModal(false);
      setAddForm({});
      // Refresh
      const itemsRes = await axios.get(`${API}/fleet/${fleetTab}`, { withCredentials: true });
      const entityKey = fleetTab.replace('-', '_');
      setItems(itemsRes.data[entityKey] || itemsRes.data[fleetTab] || []);
      const statsRes = await axios.get(`${API}/fleet/stats`, { withCredentials: true });
      setStats(statsRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this?')) return;
    try {
      await axios.delete(`${API}/fleet/${fleetTab}/${id}`, { withCredentials: true });
      toast.success('Deleted successfully!');
      setItems(items.filter(i => i[`${fleetTab.replace('-', '_').slice(0, -1)}_id`] !== id && i.driver_id !== id && i.truck_id !== id));
      const statsRes = await axios.get(`${API}/fleet/stats`, { withCredentials: true });
      setStats(statsRes.data);
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleFileUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('entity_type', fleetTab);
    
    try {
      const res = await axios.post(`${API}/fleet/import`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Imported ${res.data.imported} records!`);
      setShowUploadModal(false);
      setUploadFile(null);
      // Refresh
      const itemsRes = await axios.get(`${API}/fleet/${fleetTab}`, { withCredentials: true });
      const entityKey = fleetTab.replace('-', '_');
      setItems(itemsRes.data[entityKey] || itemsRes.data[fleetTab] || []);
      const statsRes = await axios.get(`${API}/fleet/stats`, { withCredentials: true });
      setStats(statsRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to import');
    } finally {
      setUploading(false);
    }
  };

  // Bulk import trucks or drivers from text
  const handleBulkImport = async () => {
    if (!bulkText.trim()) {
      toast.error(`Please paste ${bulkImportType} data`);
      return;
    }
    
    setImporting(true);
    try {
      const lines = bulkText.trim().split('\n');
      
      if (bulkImportType === 'trucks') {
        // Parse trucks: Unit #, Make, Model, VIN, Year
        const trucks = [];
        
        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine) continue;
          
          // Support formats: comma, tab, or multiple spaces
          let parts;
          if (trimmedLine.includes(',')) {
            parts = trimmedLine.split(',').map(p => p.trim());
          } else if (trimmedLine.includes('\t')) {
            parts = trimmedLine.split('\t').map(p => p.trim());
          } else {
            parts = trimmedLine.split(/\s{2,}/).map(p => p.trim());
          }
          
          if (parts.length >= 1 && parts[0]) {
            const truck = { unit_number: parts[0] };
            if (parts[1]) truck.make = parts[1];
            if (parts[2]) truck.model = parts[2];
            if (parts[3]) truck.vin = parts[3];
            if (parts[4]) truck.year = parts[4];
            trucks.push(truck);
          }
        }
        
        if (trucks.length === 0) {
          toast.error('No valid truck data found');
          setImporting(false);
          return;
        }
        
        const res = await axios.post(`${API}/fleet/trucks/bulk-import`, { trucks }, { withCredentials: true });
        toast.success(`Imported ${res.data.imported} trucks! (${res.data.skipped} already existed)`);
        
        // Refresh trucks
        const itemsRes = await axios.get(`${API}/fleet/trucks`, { withCredentials: true });
        setItems(itemsRes.data.trucks || []);
        
      } else if (bulkImportType === 'drivers') {
        // Parse drivers: Name, Phone, Email
        const drivers = [];
        
        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine) continue;
          
          // Support formats: comma, tab, or multiple spaces
          let parts;
          if (trimmedLine.includes(',')) {
            parts = trimmedLine.split(',').map(p => p.trim());
          } else if (trimmedLine.includes('\t')) {
            parts = trimmedLine.split('\t').map(p => p.trim());
          } else {
            parts = trimmedLine.split(/\s{2,}/).map(p => p.trim());
          }
          
          if (parts.length >= 1 && parts[0]) {
            const driver = { name: parts[0] };
            if (parts[1]) driver.phone = parts[1];
            if (parts[2]) driver.email = parts[2];
            drivers.push(driver);
          }
        }
        
        if (drivers.length === 0) {
          toast.error('No valid driver data found');
          setImporting(false);
          return;
        }
        
        const res = await axios.post(`${API}/fleet/drivers/bulk-import`, { drivers }, { withCredentials: true });
        toast.success(`Imported ${res.data.imported} drivers! (${res.data.skipped} already existed)`);
        
        // Refresh drivers
        const itemsRes = await axios.get(`${API}/fleet/drivers`, { withCredentials: true });
        setItems(itemsRes.data.drivers || []);
      }
      
      setShowBulkImport(false);
      setBulkText('');
      
      // Refresh stats
      const statsRes = await axios.get(`${API}/fleet/stats`, { withCredentials: true });
      setStats(statsRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to import ${bulkImportType}`);
    } finally {
      setImporting(false);
    }
  };

  const currentEntity = entityTypes.find(e => e.key === fleetTab);

  if (loading && fleetTab === 'overview') {
    return <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-accent" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        <button
          onClick={() => setFleetTab('overview')}
          className={`px-4 py-2 rounded-xl font-medium whitespace-nowrap transition-colors ${fleetTab === 'overview' ? 'bg-accent text-black' : 'bg-secondary hover:bg-accent/20'}`}
        >
          Overview
        </button>
        {entityTypes.map(entity => (
          <button
            key={entity.key}
            onClick={() => setFleetTab(entity.key)}
            className={`px-4 py-2 rounded-xl font-medium whitespace-nowrap transition-colors flex items-center gap-2 ${fleetTab === entity.key ? 'bg-accent text-black' : 'bg-secondary hover:bg-accent/20'}`}
          >
            <entity.icon className="w-4 h-4" />
            {entity.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {fleetTab === 'overview' && (
        <div className="grid grid-cols-3 gap-4">
          {entityTypes.map(entity => {
            const colorStyles = {
              orange: { bg: 'from-orange-500/25 to-orange-600/10', border: 'border-orange-500/30', text: 'text-orange-400' },
              cyan: { bg: 'from-cyan-500/25 to-cyan-600/10', border: 'border-cyan-500/30', text: 'text-cyan-400' },
              green: { bg: 'from-green-500/25 to-green-600/10', border: 'border-green-500/30', text: 'text-green-400' },
              blue: { bg: 'from-blue-500/25 to-blue-600/10', border: 'border-blue-500/30', text: 'text-blue-400' },
              purple: { bg: 'from-purple-500/25 to-purple-600/10', border: 'border-purple-500/30', text: 'text-purple-400' },
              pink: { bg: 'from-pink-500/25 to-pink-600/10', border: 'border-pink-500/30', text: 'text-pink-400' },
            };
            const cs = colorStyles[entity.color];
            return (
              <button
                key={entity.key}
                onClick={() => setFleetTab(entity.key)}
                className={`bg-gradient-to-br ${cs.bg} rounded-2xl p-6 border ${cs.border} text-center hover:scale-[1.02] transition-transform`}
              >
                <entity.icon className={`w-12 h-12 ${cs.text} mx-auto mb-3`} />
                <p className={`text-3xl font-bold ${cs.text}`}>{typeof stats[entity.key] === 'object' ? stats[entity.key]?.total || 0 : stats[entity.key] || 0}</p>
                <p className={`text-sm ${cs.text} opacity-70`}>{entity.label}</p>
              </button>
            );
          })}
        </div>
      )}

      {/* Entity List */}
      {fleetTab !== 'overview' && (
        <>
          {/* Action Buttons */}
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => { setAddForm({}); setShowAddModal(true); }}
              className="px-4 py-2 bg-accent text-black rounded-xl font-medium flex items-center gap-2 hover:bg-accent/90"
            >
              <Plus className="w-4 h-4" /> Add {currentEntity?.label.slice(0, -1)}
            </button>
            {fleetTab === 'trucks' && (
              <button
                onClick={() => { setBulkImportType('trucks'); setShowBulkImport(true); }}
                className="px-4 py-2 bg-orange-500 text-white rounded-xl font-medium flex items-center gap-2 hover:bg-orange-600"
              >
                <ClipboardPaste className="w-4 h-4" /> Bulk Import Trucks
              </button>
            )}
            {fleetTab === 'drivers' && (
              <button
                onClick={() => { setBulkImportType('drivers'); setShowBulkImport(true); }}
                className="px-4 py-2 bg-cyan-500 text-white rounded-xl font-medium flex items-center gap-2 hover:bg-cyan-600"
              >
                <ClipboardPaste className="w-4 h-4" /> Bulk Import Drivers
              </button>
            )}
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-4 py-2 bg-secondary rounded-xl font-medium flex items-center gap-2 hover:bg-accent/20"
            >
              <Upload className="w-4 h-4" /> Import CSV
            </button>
          </div>

          {/* Items List */}
          <div className="bg-card/60 backdrop-blur-xl rounded-2xl border border-border/30 overflow-hidden">
            <div className="p-4 border-b border-border/30 flex items-center justify-between">
              <h3 className="text-lg font-bold flex items-center gap-2">
                {currentEntity && <currentEntity.icon className="w-5 h-5" />}
                {currentEntity?.label} ({items.length})
              </h3>
            </div>
            <div className="divide-y divide-border/30 max-h-[400px] overflow-y-auto">
              {items.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  No {currentEntity?.label.toLowerCase()} found. Click "Add" or "Import CSV" to get started.
                </div>
              ) : (
                items.map((item, idx) => {
                  const idField = Object.keys(item).find(k => k.endsWith('_id'));
                  const itemId = item[idField];
                  return (
                    <div key={idx} className="p-4 flex items-center gap-4 hover:bg-accent/5">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{item.name || item.truck_number || item.title || 'Unnamed'}</p>
                        <p className="text-sm text-muted-foreground truncate">
                          {fleetTab === 'trucks' ? (
                            <>
                              {item.vin && <span className="font-mono text-xs">{item.vin}</span>}
                              {item.make && <span className="ml-2">{item.make} {item.model} {item.year}</span>}
                              {!item.vin && !item.make && 'No VIN'}
                            </>
                          ) : (
                            item.email || item.license_plate || item.address || item.phone || item.status || 'No details'
                          )}
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-lg flex-shrink-0 ${item.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                        {item.status || 'active'}
                      </span>
                      <button onClick={() => handleDelete(itemId)} className="p-2 hover:bg-red-500/20 rounded-lg text-red-400 flex-shrink-0">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowAddModal(false)}>
          <div className="bg-card rounded-2xl p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4">Add New {currentEntity?.label.slice(0, -1)}</h3>
            <div className="space-y-4">
              {currentEntity?.fields.map(field => (
                <div key={field}>
                  <label className="text-sm text-muted-foreground capitalize">{field.replace('_', ' ')}</label>
                  <input
                    type={field.includes('email') ? 'email' : field.includes('date') || field.includes('expiry') ? 'date' : 'text'}
                    value={addForm[field] || ''}
                    onChange={e => setAddForm({...addForm, [field]: e.target.value})}
                    className="w-full mt-1 px-4 py-3 rounded-xl bg-secondary border border-border focus:border-accent outline-none"
                    placeholder={field === 'status' ? 'active / inactive' : ''}
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowAddModal(false)} className="flex-1 py-3 bg-secondary rounded-xl font-medium">Cancel</button>
              <button onClick={handleAdd} className="flex-1 py-3 bg-accent text-black rounded-xl font-bold">Add</button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowUploadModal(false)}>
          <div className="bg-card rounded-2xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4">Import {currentEntity?.label} from CSV</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Upload a CSV file with columns: {currentEntity?.fields.join(', ')}
            </p>
            <div className="border-2 border-dashed border-border rounded-xl p-8 text-center mb-4">
              <input
                type="file"
                accept=".csv"
                onChange={e => setUploadFile(e.target.files?.[0] || null)}
                className="hidden"
                id="csv-upload"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <Upload className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                <p className="font-medium">{uploadFile ? uploadFile.name : 'Click to select CSV file'}</p>
                <p className="text-sm text-muted-foreground mt-1">or drag and drop</p>
              </label>
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setShowUploadModal(false); setUploadFile(null); }} className="flex-1 py-3 bg-secondary rounded-xl font-medium">Cancel</button>
              <button 
                onClick={handleFileUpload} 
                disabled={!uploadFile || uploading}
                className="flex-1 py-3 bg-accent text-black rounded-xl font-bold disabled:opacity-50"
              >
                {uploading ? 'Importing...' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Import Modal - Dynamic for Trucks or Drivers */}
      {showBulkImport && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowBulkImport(false)}>
          <div className="bg-card rounded-2xl p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
              {bulkImportType === 'trucks' ? (
                <><Truck className="w-6 h-6 text-orange-500" /> Bulk Import Trucks</>
              ) : (
                <><Users className="w-6 h-6 text-cyan-500" /> Bulk Import Drivers</>
              )}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {bulkImportType === 'trucks' 
                ? 'Paste your truck list below. Format: Unit #, Make, Model, VIN, Year'
                : 'Paste your driver list below. Format: Name, Phone, Email'}
            </p>
            
            {/* Format Examples */}
            <div className="bg-secondary/50 rounded-xl p-4 mb-4">
              <p className="text-xs font-medium text-muted-foreground mb-2">Supported formats (comma, tab, or space separated):</p>
              <div className="font-mono text-xs space-y-1 text-muted-foreground">
                {bulkImportType === 'trucks' ? (
                  <>
                    <p>UNIT123, Freightliner, Cascadia, 1HGCM82633A123456, 2022</p>
                    <p>UNIT456, Peterbilt, 579, VINHERE123456789, 2023</p>
                    <p>UNIT789</p>
                  </>
                ) : (
                  <>
                    <p>John Smith, 555-123-4567, john@email.com</p>
                    <p>Jane Doe, 555-987-6543, jane@email.com</p>
                    <p>Bob Wilson, 555-555-5555</p>
                  </>
                )}
              </div>
            </div>
            
            {/* Text Area */}
            <textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              placeholder={bulkImportType === 'trucks' 
                ? "Paste your truck list here...&#10;UNIT123, Freightliner, Cascadia, VIN123, 2022&#10;UNIT456, Peterbilt, 579, VIN456, 2023"
                : "Paste your driver list here...&#10;John Smith, 555-123-4567, john@email.com&#10;Jane Doe, 555-987-6543, jane@email.com"}
              className="w-full h-48 p-4 rounded-xl bg-secondary border border-border focus:border-accent outline-none font-mono text-sm resize-none"
            />
            
            <p className="text-xs text-muted-foreground mt-2">
              {bulkText.split('\n').filter(l => l.trim()).length} lines detected
            </p>
            
            <div className="flex gap-3 mt-4">
              <button 
                onClick={() => { setShowBulkImport(false); setBulkText(''); }} 
                className="flex-1 py-3 bg-secondary rounded-xl font-medium"
              >
                Cancel
              </button>
              <button 
                onClick={handleBulkImport}
                disabled={!bulkText.trim() || importing}
                className={`flex-1 py-3 ${bulkImportType === 'trucks' ? 'bg-orange-500' : 'bg-cyan-500'} text-white rounded-xl font-bold disabled:opacity-50 flex items-center justify-center gap-2`}
              >
                {importing ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Import {bulkImportType === 'trucks' ? 'Trucks' : 'Drivers'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
